import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import { AuthCard } from '@/features/auth/AuthPages';
import { Button, ErrorNote, Spinner, ui } from '@/components/ui';
import { accountService, ApiError } from '@/services/api';
import { businessContext } from '@/store/businessContext';
import { CategoryPicker, toChoices, type CategorySlots } from './CategoryPicker';
import { ClaimPanel } from './ClaimPanel';

const CreateSchema = z.object({
  name: z.string().trim().min(2, 'Enter your business name'),
  websiteUrl: z.string().trim().min(3, 'Enter your website'),
  countryCode: z
    .string()
    .trim()
    .length(2, 'Use a 2-letter country code')
    .toUpperCase()
    .optional()
    .or(z.literal('')),
});

export function OnboardingPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  // ?business=<slug>: arrived from "Claim this business" on the website.
  const claimSlug = params.get('business');
  const [error, setError] = useState<unknown>(null);
  const [existingSlug, setExistingSlug] = useState<string | null>(claimSlug);
  const [categories, setCategories] = useState<CategorySlots>([]);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const { register, handleSubmit, formState } = useForm({ resolver: zodResolver(CreateSchema) });

  return (
    <AuthCard title="Add your business">
      {existingSlug ? (
        <>
          <p>
            {existingSlug === claimSlug
              ? 'Verify that you manage this business to take ownership of its profile, reply to reviews and invite customers.'
              : 'A profile for this website already exists. Verify that you manage it to take ownership.'}
          </p>
          <ClaimPanel
            businessSlug={existingSlug}
            onClaimed={async (businessId) => {
              // Reload the list first: selecting a business not in it yet falls back to another.
              await qc.invalidateQueries({ queryKey: ['my-businesses'] });
              businessContext.set(businessId);
              await navigate('/');
            }}
          />
        </>
      ) : (
        <form
          className={ui.stack}
          onSubmit={handleSubmit(async (v) => {
            if (!categories.length) {
              setCategoryError('Choose the main category of your business');
              return;
            }
            try {
              setError(null);
              const created = await accountService.createBusiness({
                name: v.name,
                websiteUrl: v.websiteUrl,
                ...(v.countryCode ? { countryCode: v.countryCode } : {}),
                categories: toChoices(categories),
              });
              // Reload the list first: selecting a business not in it yet falls back to another.
              await qc.invalidateQueries({ queryKey: ['my-businesses'] });
              businessContext.set(created.id);
              await navigate('/');
            } catch (err) {
              if (err instanceof ApiError && err.code === 'BUSINESS_EXISTS' && err.details[0])
                setExistingSlug(err.details[0].message);
              else setError(err);
            }
          })}
        >
          <div className={ui.field}>
            <label htmlFor="name">Business name</label>
            <input id="name" className={ui.input} {...register('name')} />
            {formState.errors.name ? (
              <p className={ui.error}>{formState.errors.name.message}</p>
            ) : null}
          </div>
          <div className={ui.field}>
            <label htmlFor="websiteUrl">Website</label>
            <input
              id="websiteUrl"
              placeholder="example.com"
              className={ui.input}
              {...register('websiteUrl')}
            />
            {formState.errors.websiteUrl ? (
              <p className={ui.error}>{formState.errors.websiteUrl.message}</p>
            ) : null}
          </div>
          <div className={ui.field}>
            <label htmlFor="countryCode">Country (optional)</label>
            <input
              id="countryCode"
              placeholder="GB"
              maxLength={2}
              className={ui.input}
              {...register('countryCode')}
            />
          </div>
          <CategoryPicker
            value={categories}
            onChange={(next) => {
              setCategories(next);
              setCategoryError(null);
            }}
            error={categoryError}
          />
          <ErrorNote error={error} />
          <Button type="submit" disabled={formState.isSubmitting}>
            Continue
          </Button>
        </form>
      )}
      <Link to="/">Back to dashboard</Link>
    </AuthCard>
  );
}

// Landing page for the claim-verification email link.
export function ClaimVerifyPage() {
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const [state, setState] = useState<unknown>('pending');
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    accountService.verifyClaim(params.get('claim') ?? '', params.get('token') ?? undefined).then(
      async (r) => {
        await qc.invalidateQueries();
        businessContext.set(r.businessId);
        setState('ok');
      },
      (err: unknown) => {
        setState(err);
      },
    );
  }, [params, qc]);
  return (
    <AuthCard title="Verify your business">
      {state === 'pending' ? (
        <Spinner />
      ) : state === 'ok' ? (
        <p>
          You now manage this business. <Link to="/">Open the dashboard</Link>
        </p>
      ) : (
        <ErrorNote error={state} />
      )}
    </AuthCard>
  );
}

export function TeamAcceptPage() {
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const [state, setState] = useState<unknown>('pending');
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    accountService.acceptTeamInvite(params.get('token') ?? '').then(
      async (r) => {
        await qc.invalidateQueries();
        businessContext.set(r.businessId);
        setState('ok');
      },
      (err: unknown) => {
        setState(err);
      },
    );
  }, [params, qc]);
  return (
    <AuthCard title="Join a team">
      {state === 'pending' ? (
        <Spinner />
      ) : state === 'ok' ? (
        <p>
          You&apos;ve joined the team. <Link to="/">Open the dashboard</Link>
        </p>
      ) : (
        <ErrorNote error={state} />
      )}
    </AuthCard>
  );
}
